import { Stage } from '@pixi/layers';
import { sound } from '@pixi/sound';
import { Application, Container, ENV, Loader, LoaderResource, MIPMAP_MODES, SCALE_MODES, settings } from 'pixi.js';
import Stats from 'stats.js';
import { CutAway, Store, EventEmitter } from '.';
import { SCENE_STATUS } from '../constant';
import { linear } from '../util';
import Scene from './Scene';

class Game extends EventEmitter {
  public static readonly instance: Game = new Game();
  private readonly stats?: Stats;
  public readonly app: Application;
  private dom?: HTMLElement;
  public paused = true;

  public width: number = 0;
  public height: number = 0;
  public resolution: number = window.devicePixelRatio;

  private prevEpoch: number = Date.now();
  private currentEpoch: number = Date.now();
  private defaultElapsed: number = 1000 / 60;

  public scene?: Scene;
  public sceneContainer: Container;
  public sceneStatus: SCENE_STATUS;
  public cutaway?: CutAway;

  public loaded: number = 0;
  public maxLoaded: number = 0;
  public lastLoaded: string = '';

  constructor() {
    super();
    settings.SCALE_MODE = SCALE_MODES.NEAREST;
    settings.MIPMAP_TEXTURES = MIPMAP_MODES.OFF;
    settings.STRICT_TEXTURE_CACHE = true;
    settings.PREFER_ENV = ENV.WEBGL2;
    settings.FILTER_RESOLUTION = window.devicePixelRatio;
    this.app = new Application({
      sharedLoader: true,
      powerPreference: 'high-performance',
      autoStart: false,
      antialias: false,
      forceCanvas: false,
      preserveDrawingBuffer: false,
      resolution: this.resolution,
      backgroundColor: 0xffffff,
    });
    this.app.stage = new Stage();
    this.sceneContainer = new Container();
    this.sceneStatus = SCENE_STATUS.ACTIVE;

    this.app.stage.addChild(this.sceneContainer);

    requestAnimationFrame(this.update);

    window.addEventListener('resize', this.resize);

    if (process.env.NODE_ENV === 'development') {
      this.stats = new Stats();
      this.stats.dom.style.removeProperty('top');
      this.stats.dom.style.setProperty('bottom', '0px');
    }

    Loader.shared.destroy();
    sound.removeAll();

    this.on('shared', () => {
      if (this.scene) {
        this.scene.emit('shared');
      }
    });
  }

  public async preloadForLoading(): Promise<void> {
    const fonts: Array<[string, string]> = [
      // Fonts
      ['PressStart2P', '/fonts/PressStart2P.ttf'],
      ['gaegu', '/fonts/Gaegu-Bold.ttf'],
      [
        'ff-providence-sans-web-pro',
        'https://use.typekit.net/af/de26c0/0000000000000000773599ea/30/l?primer=7cdcb44be4a7db8877ffa5c0007b8dd865b3bbc383831fe2ea177f62257a9191&fvd=n7&v=3',
      ],
    ];
    const assets: Array<string> = ['/textures/bg/bg_loading.png'];

    for (const [fontName, url] of fonts) {
      const font = new FontFace(fontName, `url(${url})`);
      await font.load();
      document.fonts.add(font);
    }

    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
      }
    }

    await new Promise<void>((resolve) => {
      Loader.shared.onComplete.once(() => {
        this.loaded = Object.keys(Loader.shared.resources).length;
        resolve();
      });
      Loader.shared.load();
    });
  }

  public async preload(): Promise<void> {
    const assets: Array<string> = [
      // Audios
      '/audio/bgm_main.mp3',
      '/audio/btn.mp3',
      '/audio/item_touch.mp3',
      '/audio/swipe.mp3',
      '/audio/btn_start.mp3',
      '/audio/gameover_popup.mp3',
      '/audio/getting_item.mp3',
      '/audio/bonus.mp3',

      // BG
      '/textures/bg/bg_main.png',
      '/textures/bg/bg_main_select.png',
      '/textures/bg/bg_multiplay.png',
      '/textures/fx/fx2.webp',
      // '/textures/etc/snow_1.png',
      // '/textures/etc/snow_2.png',

      // UI
      '/textures/ui/btn_multi.png',
      '/textures/ui/locked_0.png',
      '/textures/ui/locked_1.png',
      '/textures/ui/locked_2.png',
      '/textures/ui/locked_3.png',
      '/textures/ui/locked_4.png',
      '/textures/ui/locked_5.png',
      '/textures/ui/locked_6.png',
      '/textures/ui/unlocked_6.png',
      '/textures/ui/unlocked_5.png',
      '/textures/ui/unlocked_4.png',
      '/textures/ui/unlocked_3.png',
      '/textures/ui/unlocked_2.png',
      '/textures/ui/unlocked_1.png',
      '/textures/ui/unlocked_0.png',
      '/textures/ui/selected.png',
      '/textures/ui/item_1.png',
      '/textures/ui/item_2.png',
      '/textures/ui/item_3.png',
      '/textures/ui/item_4.png',
      '/textures/ui/item_5.png',
      '/textures/ui/item_6.png',
      '/textures/ui/btn_start.png',
      '/textures/ui/home.png',
      '/textures/ui/inventory.png',
      '/textures/ui/hint.png',
      '/textures/ui/popup_score.png',
      '/textures/etc/arrow.png',
      '/textures/ui/flag.png',
      '/textures/ui/heart.png',
      '/textures/ui/mouse.png',
      '/textures/ui/notification_nini.png',
      '/textures/ui/popup_item_get.png',
      '/textures/ui/pause.png',
      '/textures/ui/start.png',
      '/textures/ui/ninimo_1.png',
      '/textures/ui/ninimo_2.png',
      '/textures/ui/ninimo_3.png',
      '/textures/ui/ninimo_4.png',
      '/textures/ui/ninimo_5.png',
      '/textures/ui/stage_1.png',
      '/textures/ui/stage_2.png',
      '/textures/ui/stage_3.png',
      '/textures/ui/stage_4.png',
      '/textures/ui/trophy.png',
      '/textures/ui/user.png',
      '/textures/ui/btn_base.png',
      '/textures/ui/popup_name_change.png',
      '/textures/ui/popup_ranking_board.png',
      '/textures/ui/btn_long.png',
      '/textures/ui/emotion_background.png',
      '/textures/ui/result.png',

      '/textures/ui/btn_restart.png',
      '/textures/ui/btn_long_home.png',
      '/textures/ui/btn_play_too.png',
      '/textures/ui/share_white.png',
      '/textures/ui/popup_all_clear.png',
      '/textures/ui/background_effect_2.png',
      '/textures/ui/background_effect.png',
      // '/textures/ui/snow_effect.png',

      // Icons
      '/textures/icons/instagram.png',
      '/textures/icons/volume_0.png',
      '/textures/icons/volume_1.png',
      `/textures/objects/projectile/projectile_3.png`,
      '/textures/etc/star.png',
    ];

    for (const asset of assets) {
      if (!Loader.shared.resources[asset]) {
        Loader.shared.add(asset, asset);
      }
    }

    return new Promise((resolve) => {
      Loader.shared.onLoad.add((loader: Loader, resource: LoaderResource) => {
        this.loaded++;
        this.lastLoaded = resource.name;
      });
      Loader.shared.onComplete.once(() => {
        resolve();
      });
      Loader.shared.load();
      this.maxLoaded = Object.keys(Loader.shared.resources).length;
    });
  }

  public readonly update = (): void => {
    this.currentEpoch = Date.now();
    if (this.stats) this.stats.begin();
    if (!this.paused) {
      let elapsed = (this.currentEpoch - this.prevEpoch) / this.defaultElapsed;

      elapsed = elapsed > 3 ? 3 : elapsed;

      if (this.sceneStatus !== SCENE_STATUS.ACTIVE) {
        this?.cutaway?.update(elapsed);
      }

      if (this.scene) {
        this.scene.update(elapsed);
        if (this.scene.isRunning) {
          this.scene.duration += elapsed * this.defaultElapsed;
        }
      }
      this.app.render();

      // Volume Controll
      if (Store.instance.state.volume === 1) {
        if (this.sceneStatus === SCENE_STATUS.ACTIVE) {
          if (sound.volumeAll < 1) {
            sound.volumeAll += 0.01 * elapsed;
          }
          if (sound.volumeAll >= 1) {
            sound.volumeAll = 1;
          }
        } else {
          if (sound.volumeAll > 0) {
            sound.volumeAll -= 0.01 * elapsed;
          }
          if (sound.volumeAll <= 0) {
            sound.volumeAll = 0;
          }
        }
      }
    }
    if (this.stats) this.stats.end();
    this.prevEpoch = this.currentEpoch;
    requestAnimationFrame(this.update);
  };

  public readonly resize = (): void => {
    if (this.dom) {
      this.app.renderer.resize(this.dom.clientWidth, this.dom.clientHeight);
      this.app.render();

      this.width = this.app.view.width / this.resolution;
      this.height = this.app.view.height / this.resolution;
    }

    if (this.scene) {
      this.scene.resize();
    }
  };

  public start(): void {
    this.paused = false;
    sound.resumeAll();
    if (this.scene && this.scene.isStarted) {
      this.scene.isRunning = true;
    }
  }

  public stop(): void {
    this.paused = true;
    this.app.render();
    sound.pauseAll();
    if (this.scene && this.scene.isStarted) {
      this.scene.isRunning = false;
    }
  }

  public destroy(): void {
    super.destroy();
    window.removeEventListener('resize', this.resize);
  }

  public append(dom: HTMLElement): void {
    this.dom = dom;
    if (this.stats) {
      this.dom.appendChild(this.stats.dom);
    }
    this.dom.appendChild(this.app.view);
    this.resize();
  }

  public async changeScene(SceneConstructor: typeof Scene): Promise<void> {
    const beforeScene = this.scene;

    if (this.cutaway) {
      this.cutaway.text.text = 'Loading 0%';
    }

    if (beforeScene) {
      this.app.stage.interactiveChildren = false;
      await this.sceneFadeOut();
      sound.stopAll();
    }

    // Loading...
    await SceneConstructor.preload((currentValue: number, maxValue: number): void => {
      if (this.cutaway) {
        this.cutaway.setPercentage(currentValue, maxValue);
        this.cutaway.update(1);
      }
    });

    if (beforeScene) {
      beforeScene.destroy();
      this.sceneContainer.removeChild(beforeScene.container);
    }

    const scene = new SceneConstructor(this);
    this.scene = scene;

    this.sceneContainer.addChild(scene.container);
    if (beforeScene) {
      await this.sceneFadeIn();
    }

    this.scene.onBegin();
    this.app.stage.interactiveChildren = true;
  }

  private async sceneFadeIn(): Promise<void> {
    return new Promise((resolve) => {
      this?.cutaway?.setFlow(linear, this.cutaway.duration, 0);
      this.sceneStatus = SCENE_STATUS.FADE_IN;

      this?.cutaway?.emitter.on('done', () => {
        this.sceneStatus = SCENE_STATUS.ACTIVE;
        this?.cutaway?.emitter.off('done');
        if (this.cutaway) this.app.stage.removeChild(this.cutaway);
        resolve();
      });
    });
  }

  private async sceneFadeOut(): Promise<void> {
    return new Promise((resolve) => {
      if (this.cutaway) this.app.stage.addChild(this.cutaway);
      this?.cutaway?.setFlow(linear, 0, this.cutaway.duration);
      this.sceneStatus = SCENE_STATUS.FADE_OUT;

      this?.cutaway?.emitter.on('done', () => {
        this.sceneStatus = SCENE_STATUS.ACTIVE;
        this?.cutaway?.emitter.off('done');
        resolve();
      });
    });
  }
}

export default Game;
